// [2026-10-15] ADDED — INC-279 IncAssist C1 (CONTRACTS.md "Round 1 → C1", FV): the pure model behind the panel's one
// list of questions (starters, live follow-ups and "+ Get a question" cards, newest on top, no cap), and the
// `copilotUi` redux state: cleared ids (kept across close and reopen), the last clear for Undo, unread ids, the
// order in which each id first appeared, the toast, the flash. No React, no redux, so node can test it.

// Same normalisation as interviewReducer.js normalizeCopilotQuestion (text with no Latin letters/digits falls back
// to its trimmed lower-case form, never an empty key).
export const normalizeCopilotQuestion = (text) =>
    (text || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim() || (text || '').trim().toLowerCase();

// CONTRACTS C1: the stable suggestion id is `q.id || 'h:' + normalised text` (the live follow-ups have no id).
export const suggestionId = (q) => (q && q.id ? String(q.id) : `h:${normalizeCopilotQuestion(q?.question)}`);

const LEVELS = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
export const levelOf = (difficulty) => LEVELS[String(difficulty || '').toLowerCase()] || '';

export const COPILOT_UI_INIT = {
    tab: 'suggestions',
    cleared: {},
    undo: null, // { ids, toastId } — the last clear, for Undo
    unread: {},
    firstSeen: {}, // id → order number (higher = newer)
    seq: 0,
    proctoringSeenAt: 0,
    open: false,
    toast: null, // { id, message, undo }
    flash: null, // { id, key }
    enteringId: null,
    // [2026-10-23] ADDED — INC-279 IncAssist (Rujal, 2026-10-23): the ids of the newest batch of cards (id → true). Only
    // these cards show the "New" chip; the next batch replaces it. Kept in redux, so it survives closing the panel.
    latestBatch: {},
};

/** New ids get the next order numbers (in the given order, the first one newest) and, unless `read`, are unread. */
export function registerIds(ui, ids, { read = false } = {}) {
    const fresh = (ids || []).filter((id) => id && ui.firstSeen[id] === undefined);
    if (!fresh.length) return ui;
    const firstSeen = { ...ui.firstSeen };
    const unread = { ...ui.unread };
    let seq = ui.seq;
    // The first id of the batch is the newest: number the batch from its end.
    [...fresh].reverse().forEach((id) => {
        seq += 1;
        firstSeen[id] = seq;
        if (!read) unread[id] = true;
    });
    // [2026-10-23] CHANGED — INC-279 IncAssist (Rujal, 2026-10-23): a registration of new cards is one "batch" (the
    // starters at the start, then each follow-up / code / project batch); it becomes the only batch with the "New"
    // chip. A `read` registration (already read) does not take the chip away from the current batch. Was:
    // return { ...ui, firstSeen, unread, seq };
    if (read) return { ...ui, firstSeen, unread, seq };
    const latestBatch = {};
    fresh.forEach((id) => {
        latestBatch[id] = true;
    });
    return { ...ui, firstSeen, unread, seq, latestBatch };
}

// [2026-10-23] ADDED — INC-279 IncAssist (Rujal, 2026-10-23): does this card show the "New" chip? Only the newest batch,
// and never a cleared card (so after "Clear all" no card is New until the next batch arrives).
export const isNewCard = (ui, id) =>
    !!ui && !!id && !!ui.latestBatch && ui.latestBatch[id] === true && !(ui.cleared && ui.cleared[id]);

export function markRead(ui, id) {
    if (!ui.unread[id]) return ui;
    const unread = { ...ui.unread };
    delete unread[id];
    return { ...ui, unread };
}

let toastSeq = 0;
export const nextToast = (message, undo = false) => {
    toastSeq += 1;
    return { id: toastSeq, message, undo };
};

/** Clear cards: they stay cleared (also across close and reopen) until Undo. */
export function clearIds(ui, ids, message) {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return ui;
    const cleared = { ...ui.cleared };
    const unread = { ...ui.unread };
    list.forEach((id) => {
        cleared[id] = true;
        delete unread[id];
    });
    const toast = nextToast(message, true);
    return { ...ui, cleared, unread, undo: { ids: list, toastId: toast.id }, toast };
}

/** Undo the last clear (only while its toast is the current one). */
export function undoClear(ui) {
    if (!ui.undo) return ui;
    const cleared = { ...ui.cleared };
    ui.undo.ids.forEach((id) => delete cleared[id]);
    return { ...ui, cleared, undo: null, toast: null };
}

// [2026-10-21] ADDED — INC-279 IncAssist: the chip text on starter cards.
export const STARTER_TAG = 'Resume';

// [2026-10-23] ADDED — INC-279 IncAssist (Rujal, 2026-10-23: starters must always be 4): starters written from the
// job description (a job skill the resume does not show) carry a "JD" chip instead of "Resume". The plan marks them
// basis 'jd' (and intent 'gap'); a starter with no basis (every plan before today) keeps "Resume".
export const STARTER_JD_TAG = 'JD';
export const starterTagOf = (q) =>
    q && (q.basis === 'jd' || (!q.basis && q.intent === 'gap')) ? STARTER_JD_TAG : STARTER_TAG;

/**
 * The one list, newest first: every source's questions, de-duplicated by normalised text (the first id seen wins),
 * minus cleared ones. Ids never registered yet sort on top (they are about to be registered).
 * starters: [{id, question, skill, skill_type, difficulty_level, resume_hint}] (C2 plan)
 * followUps: the store's aiCopilotQuestions (newest first; no id)
 * skillQuestions: "+ Get a question" results (newest first)
 */
// [2026-10-16] CHANGED — INC-279 C5: + codeQuestions (the "Coding" cards, source 'code_follow_up'), on top of the
// same one list. Was:
// export function buildSuggestionList({ starters = [], followUps = [], skillQuestions = [], ui = COPILOT_UI_INIT }) {
//     const all = [
//         ...(Array.isArray(skillQuestions) ? skillQuestions : []).map((q) => ({ q, source: 'skill_question' })),
// [2026-10-17] CHANGED — INC-279 IncAssist RS: + projectQuestions (the "Project: <repo>" cards, source 'repo_scan'),
// on top of the same one list. Was (C5):
// export function buildSuggestionList({
//     starters = [],
//     followUps = [],
//     skillQuestions = [],
//     codeQuestions = [],
//     ui = COPILOT_UI_INIT,
// }) {
//     const all = [
//         ...(Array.isArray(codeQuestions) ? codeQuestions : []).map((q) => ({ q, source: 'code_follow_up' })),
export function buildSuggestionList({
    starters = [],
    followUps = [],
    skillQuestions = [],
    codeQuestions = [],
    projectQuestions = [],
    ui = COPILOT_UI_INIT,
}) {
    const all = [
        ...(Array.isArray(projectQuestions) ? projectQuestions : []).map((q) => ({ q, source: 'repo_scan' })),
        ...(Array.isArray(codeQuestions) ? codeQuestions : []).map((q) => ({ q, source: 'code_follow_up' })),
        ...(Array.isArray(skillQuestions) ? skillQuestions : []).map((q) => ({ q, source: 'skill_question' })),
        ...(Array.isArray(followUps) ? followUps : []).map((q) => ({ q, source: 'follow_up' })),
        ...(Array.isArray(starters) ? starters : []).map((q) => ({ q, source: 'starter' })),
    ].filter(({ q }) => q && typeof q.question === 'string' && q.question.trim());
    const byText = new Map();
    all.forEach((entry) => {
        const id = suggestionId(entry.q);
        const key = normalizeCopilotQuestion(entry.q.question);
        const prev = byText.get(key);
        const rank = ui.firstSeen[id] ?? Infinity;
        if (!prev || rank < prev.rank) byText.set(key, { ...entry, id, rank });
    });
    return [...byText.values()]
        .filter((e) => !ui.cleared[e.id])
        .sort((a, b) => b.rank - a.rank)
        .map(({ q, id, source }) => ({
            id,
            source,
            question: q.question.trim(),
            // [2026-10-28] CHANGED — INC-1993: a code card shows the AI's real skill (job_skill, e.g. "Python"), and
            // "Coding" moves to its source chip (tag, below); no skill chip when the AI matched none. Was:
            // skill: q.skill || '',
            skill: source === 'code_follow_up' ? codeCardSkillOf(q) : q.skill || '',
            level: levelOf(q.difficulty_level),
            resumeHint: typeof q.resume_hint === 'string' && q.resume_hint.trim() ? q.resume_hint.trim() : undefined,
            // [2026-10-21] ADDED — INC-279 IncAssist (Rujal, 2026-10-21): starters (from the resume plan) carry a visible
            // "Resume" chip; every other card has no tag (unchanged).
            // [2026-10-23] CHANGED — INC-279 IncAssist: "JD" for a JD-based starter, else "Resume" as before. Was:
            // tag: source === 'starter' ? STARTER_TAG : undefined,
            // [2026-10-28] CHANGED — INC-1993: + the "Coding" source chip on code cards. Was:
            // tag: source === 'starter' ? starterTagOf(q) : undefined,
            tag: cardTagOf(source, q),
            promptVersion: q.prompt_version,
            intent: q.intent,
            unread: !!ui.unread[id],
            // [2026-10-23] ADDED — INC-279 IncAssist (Rujal, 2026-10-23): the "New" chip, on the newest batch only
            isNew: isNewCard(ui, id),
        }));
}

// [2026-10-28] ADDED — INC-1993: a code card's chips = difficulty (from the AI), the AI's real skill, and the "Coding"
// source chip ("tags (difficulty, skill, source)"). Declared as functions so buildSuggestionList above can use them.
function codeCardSkillOf(q) {
    const skill = typeof q?.job_skill === 'string' ? q.job_skill.trim() : '';
    return skill && skill !== CODE_QUESTION_TAG ? skill : '';
}
// [2026-10-29] CHANGED — INC-2052 (Rujal, 2026-10-29: "just add a GitHub tag", no skill mapping): a GitHub project card
// (source 'repo_scan') now carries a "GitHub" tag chip; its "Project: <repo>" chip and everything else stay. Was:
// function cardTagOf(source, q) {
//     if (source === 'starter') return starterTagOf(q);
//     if (source === 'code_follow_up') return CODE_QUESTION_TAG;
//     return undefined;
// }
function cardTagOf(source, q) {
    if (source === 'starter') return starterTagOf(q);
    if (source === 'code_follow_up') return CODE_QUESTION_TAG;
    if (source === 'repo_scan') return GITHUB_QUESTION_TAG;
    return undefined;
}
export { codeCardSkillOf, cardTagOf };
// [2026-10-29] ADDED — INC-2052: the tag chip of a GitHub project card
export const GITHUB_QUESTION_TAG = 'GitHub';

/** Ids in the list that were never registered (in list order, newest first). */
export const unregisteredIds = (items, ui) => items.filter((i) => ui.firstSeen[i.id] === undefined).map((i) => i.id);

/** Skill coverage for the two cards: the counted skills (getCopilotState), else the job's skills at 0. */
export function coverageOf(skillCounts, jobSkills) {
    const counted = Array.isArray(skillCounts) ? skillCounts : [];
    const source = counted.length
        ? counted.map((s) => ({ name: s.name, type: s.skill_type, asked: Number(s.count) || 0 }))
        : (Array.isArray(jobSkills) ? jobSkills : [])
              .filter((s) => s?.skill && (s.skillType === 'mandatory' || s.skillType === 'optional'))
              .map((s) => ({ name: s.skill, type: s.skillType, asked: 0 }));
    const pick = (type) => source.filter((s) => s.type === type && s.name).map(({ name, asked }) => ({ name, asked }));
    return { mandatory: pick('mandatory'), optional: pick('optional') };
}

/** "+ Get a question": the card already in the list for this skill (case-insensitive), if any. */
export const cardForSkill = (items, skill) =>
    items.find((i) => i.skill && i.skill.toLowerCase() === String(skill || '').toLowerCase()) || null;

/** The skill-question reply as a list item (newest first in copilotSkillQuestions). */
export function addSkillQuestion(list, question) {
    if (!question || typeof question.question !== 'string' || !question.question.trim()) return list;
    const item = {
        id: question.id ? `sq:${question.id}` : `sq:${normalizeCopilotQuestion(question.question)}`,
        question: question.question,
        skill: question.skill,
        skill_type: question.skill_type,
        difficulty_level: question.difficulty_level,
        resume_hint: question.resume_hint,
    };
    if ((list || []).some((q) => q.id === item.id)) return list;
    return [item, ...(list || [])];
}

// [2026-10-16] ADDED — INC-279 IncAssist C5: the code follow-up reply as list items (newest first in
// copilotCodeQuestions). The card's visible tag is "Coding"; the job skill the AI matched is kept as job_skill (data
// only). Q1 is placed above Q2. An id already in the list is not added again.
export const CODE_QUESTION_TAG = 'Coding';
// [2026-10-21] ADDED — Rujal: at most 2 Coding cards per button click.
export const MAX_CODE_QUESTIONS_PER_CLICK = 2;
export function addCodeQuestions(list, questions, promptVersion) {
    const current = Array.isArray(list) ? list : [];
    const fresh = (Array.isArray(questions) ? questions : [])
        .filter((q) => q && typeof q.question === 'string' && q.question.trim())
        .sort((a, b) => (Number(a.slot) || 0) - (Number(b.slot) || 0))
        // [2026-10-21] ADDED — Rujal: each "Generate code questions" click shows exactly 2 (VFS already sends at most 2)
        .slice(0, MAX_CODE_QUESTIONS_PER_CLICK)
        .map((q) => ({
            id: q.id ? `cf:${String(q.id).replace(/^cf:/, '')}` : `cf:${normalizeCopilotQuestion(q.question)}`,
            question: q.question.trim(),
            skill: CODE_QUESTION_TAG,
            job_skill: q.skill || null,
            difficulty_level: q.difficulty_level,
            intent: q.intent,
            prompt_version: promptVersion,
        }))
        .filter((item) => !current.some((q) => q.id === item.id));
    return fresh.length ? [...fresh, ...current] : current;
}

// [2026-10-17] ADDED — INC-279 IncAssist RS (Project Deep-Dive from the candidate's GitHub): getCopilotState.repo_scan
// as list items. Each card's tag is "Project: <repo>"; the file it is about is the card's hover tooltip (the starters'
// resume_hint slot, as the mock has no extra line on a card); 4-5 cards, in the order the AI wrote them (they arrive
// after the starters, so they register as the newest and sit at the top of the list, unread).
export const PROJECT_TAG_PREFIX = 'Project: ';
export const PROJECT_QUESTIONS_READY = 'Project questions ready';
export const NO_PUBLIC_REPO_TEXT = 'No public project repo found';
const MAX_PROJECT_QUESTIONS = 5;

export function projectQuestionsOf(repoScan) {
    if (!repoScan || repoScan.status !== 'ready' || !Array.isArray(repoScan.questions)) return [];
    const repo = typeof repoScan.repo_name === 'string' ? repoScan.repo_name.trim() : '';
    return repoScan.questions
        .filter((q) => q && typeof q.question === 'string' && q.question.trim())
        .slice(0, MAX_PROJECT_QUESTIONS)
        .map((q, i) => ({
            id: `rs:${String(q.id || i + 1).replace(/^rs:/, '')}`,
            question: q.question.trim(),
            skill: repo ? `${PROJECT_TAG_PREFIX}${repo}` : PROJECT_TAG_PREFIX.trim().replace(/:$/, ''),
            difficulty_level: q.difficulty_level,
            resume_hint: typeof q.file === 'string' && q.file.trim() ? q.file.trim() : undefined,
            intent: q.angle,
        }));
}

/** The toast is due once, when this session saw the scan go from not-ready (running / none) to ready with cards.
 *  A reload that finds it already ready shows no toast (the cards are still unread). */
export const projectToastDue = (prevStatus, repoScan) =>
    (prevStatus === 'running' || prevStatus === 'none') && projectQuestionsOf(repoScan).length > 0;

// [2026-10-17] ADDED — INC-279 RS add-on: the project part of the panel (cards, "Project questions ready", the quiet
// "No public project repo found" line) exists only with IncAssist AND "GitHub Project Questions"
// (copilotGithubProjectPermission, ANDed in MainPage). Off → no repo scan at all, so nothing about projects appears.
export const repoScanIfEnabled = (enabled, repoScan) => (enabled === true ? repoScan : null);

/** The quiet line: only when the scan found no public project repo for this candidate. */
export const showNoPublicRepo = (repoScan) => !!repoScan && repoScan.status === 'no_repo';

// [2026-10-18] ADDED — INC-279 IncAssist (Rujal, 2026-10-18): the "GitHub" button in the Follow-up Questions header. Shown
// only with "GitHub Project Questions" (copilotGithubProjectPermission, ANDed with IncAssist in MainPage). A click:
//   - no scan yet (or a failed one) → start it now (once: a second click while it starts or runs does nothing);
//   - a scan is running → nothing new (the "Reading…" line shows);
//   - the cards are ready → scroll to and flash them;
//   - no public repo (or nothing to ask) → the quiet "No public project repo found" line.
// The background auto-scan from /getCopilotStarters is unchanged. Pure, so node can test it.
export const GITHUB_BUTTON_LABEL = 'GitHub';
export const GITHUB_BUTTON_TIP = "Questions from the candidate's GitHub project";
export const READING_PROJECT_TEXT = "Reading the candidate's GitHub project…";
export const GITHUB_BUTTON_INIT = Object.freeze({ asked: false, pending: false });

export const showGithubButton = (enabled, canStart) => enabled === true && canStart === true;

/** 'start' | 'reading' | 'show' | 'no_repo' for the current scan state and a start still in flight. */
export function githubButtonAction(repoScan, pending) {
    if (pending) return 'reading';
    const status = repoScan && repoScan.status;
    if (status === 'running') return 'reading';
    if (status === 'ready' && projectQuestionsOf(repoScan).length) return 'show';
    if (status === 'ready' || status === 'no_repo' || status === 'skipped') return 'no_repo';
    return 'start'; // none, failed, or no state yet
}

/** One click. effect: 'start' (call the route), 'flash' (scroll to the project cards) or null. */
export function githubClick(button, repoScan) {
    const b = button || GITHUB_BUTTON_INIT;
    const action = githubButtonAction(repoScan, b.pending);
    if (action === 'start') return { state: { asked: true, pending: true }, effect: 'start' };
    return { state: { ...b, asked: true }, effect: action === 'show' ? 'flash' : null };
}

/** The route answered. 'running' → stay pending until the state shows the scan; anything else → done. */
export const githubAfterReply = (button, replyStatus) =>
    replyStatus === 'running' ? button || GITHUB_BUTTON_INIT : { ...(button || GITHUB_BUTTON_INIT), pending: false };

/** A new state arrived: once it shows the scan (running or finished), the start is no longer pending. */
export function githubAfterState(button, repoScan) {
    const b = button || GITHUB_BUTTON_INIT;
    const status = repoScan && repoScan.status;
    return b.pending && status && status !== 'none' ? { ...b, pending: false } : b;
}

/** The line under the header: "Reading…" only after a click, while the start or the scan runs. */
export function githubNote(button, repoScan) {
    const b = button || GITHUB_BUTTON_INIT;
    if (!b.asked) return null;
    return githubButtonAction(repoScan, b.pending) === 'reading' ? READING_PROJECT_TEXT : null;
}

/** The quiet line: the scan found no public repo, or (after a click) it ended with nothing to show (no resume, or no
 *  question). A failed scan shows nothing (a click may start it again). */
export function showNoPublicRepoAfterAsk(button, repoScan) {
    if (showNoPublicRepo(repoScan)) return true;
    const b = button || GITHUB_BUTTON_INIT;
    if (!b.asked || b.pending || !repoScan) return false;
    return githubButtonAction(repoScan, false) === 'no_repo';
}

/** The first project card's id (the list is newest first), for the scroll-and-flash. */
export const firstProjectCardId = (items) => {
    const card = (Array.isArray(items) ? items : []).find((i) => i && i.source === 'repo_scan');
    return card ? card.id : null;
};

// [2026-10-19] ADDED — INC-279 IncAssist (Rujal, 2026-10-19, live test on test): the one background repo scan stays, but
// nothing about the candidate's GitHub project shows until the interviewer clicks the "GitHub" button: no project card,
// no unread badge for them, no "Project questions ready" toast, no quiet "No public project repo found" line. The click
// is remembered in copilotUi.projectsRevealed (redux), so closing and reopening the panel keeps the cards. Pure.
/** Has the interviewer clicked "GitHub" in this interview? */
export const projectsRevealed = (ui) => !!ui && ui.projectsRevealed === true;

/** The scan to show in the panel: null until the click (the scan itself still runs in the background). */
export const repoScanAfterClick = (revealed, repoScan) => (revealed === true ? repoScan || null : null);

/** After a click whose cards were not ready yet, flash the first project card once it appears. */
export const projectFlashDue = (waiting, items) => (waiting === true ? firstProjectCardId(items) : null);

// [2026-10-23] ADDED — INC-279 IncAssist (Rujal, 2026-10-23): the 4 starter questions appear on their own, with no click:
// 2 from the candidate's GitHub project (the one background repo scan) + 2 from the resume plan (the planner writes 4;
// the JD fill tops it up when the resume cannot). This reverses the 2026-10-19 "GitHub" click: project cards are shown
// as soon as they are ready, and the button is no longer in the header.
//   - GitHub ready with questions → plan[0..1] + project[0..1] (if one side is short, the other fills up to 4);
//   - GitHub still running (or its state not known yet) → plan[0..1] now, the 2 project cards when the scan ends;
//   - GitHub off (no add-on), no repo, skipped, failed, or ready with nothing → plan[0..3];
//   - a scan that is still not finished after GITHUB_STARTERS_WAIT_MS counts as "nothing" (fail-open: plan[0..3]).
// A card already on screen (registered in copilotUi.firstSeen) is never taken away again. Pure, so node can test it.
export const GITHUB_STARTERS_AUTO = true; // Rujal, 2026-10-23: GitHub starters are automatic (no "GitHub" click)
export const STARTERS_SHOWN = 4;
export const GITHUB_STARTERS_SHOWN = 2;
// VFS REPO_SCAN_BUDGET_MS (180 s, aiCopilotRepoScan.ts) plus a margin for the doorbell and the state poll.
export const GITHUB_STARTERS_WAIT_MS = 200000;
const GITHUB_STARTERS_PENDING = ['unknown', 'none', 'running'];
const PLAN_NOT_DONE = ['none', 'running', 'unknown'];

/** 'off' | 'pending' | 'ready' | 'nothing' for the panel's (already gated) repo scan. */
export function githubStartersState(enabled, repoScan) {
    if (enabled !== true || !repoScan) return 'off';
    const status = repoScan.status;
    if (status === 'ready' && projectQuestionsOf(repoScan).length) return 'ready';
    if (!status || GITHUB_STARTERS_PENDING.includes(status)) return 'pending';
    return 'nothing'; // off (server side), no_repo, skipped, failed, or ready with no question
}

const hasText = (q) => q && typeof q.question === 'string' && q.question.trim();

/**
 * Which starter cards to show. planStarters: the plan's starters (empty until it is ready); planStatus: the plan's
 * status; projectQuestions: projectQuestionsOf(repoScan); githubState: githubStartersState(...); waitOver: the panel's
 * GITHUB_STARTERS_WAIT_MS timer ran out; firstSeen: copilotUi.firstSeen. Returns { starters, projectQuestions }.
 */
export function pickStarterCards({
    planStarters = [],
    planStatus,
    projectQuestions = [],
    githubState = 'off',
    waitOver = false,
    firstSeen = {},
} = {}) {
    const plan = (Array.isArray(planStarters) ? planStarters : []).filter(hasText);
    const project = (Array.isArray(projectQuestions) ? projectQuestions : []).filter(hasText);
    const planDone = !!planStatus && !PLAN_NOT_DONE.includes(planStatus);
    let planTake = STARTERS_SHOWN;
    let projectTake = 0;
    if (githubState === 'ready') {
        projectTake = Math.min(GITHUB_STARTERS_SHOWN, project.length);
        planTake = Math.min(STARTERS_SHOWN - projectTake, plan.length);
        // A short plan (it has ended) leaves room: more project cards fill it, up to 4 in all.
        if (planDone && planTake + projectTake < STARTERS_SHOWN) {
            projectTake = Math.min(project.length, STARTERS_SHOWN - planTake);
        }
    } else if (githubState === 'pending' && waitOver !== true) {
        planTake = STARTERS_SHOWN - GITHUB_STARTERS_SHOWN;
    }
    const seen = (q) => !!firstSeen && firstSeen[suggestionId(q)] !== undefined;
    return {
        starters: plan.filter((q, i) => i < planTake || seen(q)),
        projectQuestions: project.filter((q, i) => i < projectTake || seen(q)),
    };
}

// [2026-10-23] ADDED — INC-279 IncAssist (Rujal, 2026-10-23): "Add to screening questions" from an IncAssist card refused
// any question over 100 characters. The 100 is the interview's screeningQuestionsSummary.questionLength.max, which
// node-engine writes at scheduling (calendarCommonService.ts / calendarInviteService.ts) and interview-system writes at
// re-invite (InterviewService.ts) for IncVid's own Feedback-tab typing box. Before the interview there is no upper
// limit: node-engine's create-interview and position Joi rules check characters only, never a length. So the IncAssist
// add uses the same "no 100 cap" rule. The one ceiling left is VFS /addCopilotQuestionToPosition's own 1000
// (MAX_POSITION_QUESTION_CHARS, aiCopilotSuggestions.ts), so the screening list and the position bank accept the same
// questions. The interview's minimum (questionLength.min) still applies. Returns the refusal text, or null.
export const COPILOT_SCREENING_MAX_CHARS = 1000;
export function copilotScreeningLengthError(text, questionLength) {
    const length = typeof text === 'string' ? text.length : 0;
    const min = Number(questionLength && questionLength.min) || 0;
    if (min && length < min) return `Question can't be less than ${min} characters.`;
    if (length > COPILOT_SCREENING_MAX_CHARS)
        return `Question can't be more than ${COPILOT_SCREENING_MAX_CHARS} characters.`;
    return null;
}
